import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { IconButton, InputAdornment } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import HttpService from "../../services/HttpService";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import InputMask from 'react-input-mask';
import { NumericFormat } from 'react-number-format';

export function currencyFormat(value) {
  return value === undefined || value === null || value === ""
    ? ""
    : Number(value).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
}

export const inputNumericMask = {
                    thousandSeparator: true,
                    prefix: "$",
                    decimalScale: 2,
                    fixedDecimalScale: true,
                    allowNegative: false,
                  };

export default function CRMTextField({
  label,
  field,
  entity,
  entityId,
  fieldName,
  editorType = "text", // ["text", "select", [add more requires future implementation]]
  selectOptions = [],
  onChange,
  loading,
  outputFormat,
  inputMask,
  inputNumericMask,
  ...props
}) {
  const [unlocked, setUnlocked] = useState(false);
  const [forceViewLoading, setForceViewLoading] = useState(false);
  const [unlockError, setUnlockError] = useState("");
  const [unlockedValue, setUnlockedValue] = useState("");
  const [editValue, setEditValue] = useState(field?.value ?? "");
  const [isEditing, setIsEditing] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);
  const inputRef = useRef(null);
  const editable = typeof onChange === "function";

  const getDisplayValue = () => {
    if (unlockError !== "") return unlockError;
    if (isLocked) return "••••••••";
    if (unlocked) return unlockedValue;
    if (typeof outputFormat === "function") return outputFormat(field?.value);
    return field?.value;
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select && inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    if (isEditing && editorType === "select") {
      setSelectOpen(true);
    }
  }, [isEditing, editorType]);

if (loading)  return (
  <Box
    display="flex"
    alignItems="flex-end"
    sx={{
      minHeight: 40,
      mb: props.margin === "none" ? 0 : 1.5, 
      width: props.fullWidth === false ? "auto" : "100%",
    }}
  >
    <CircularProgress size={20} sx={{ mr: 1 }} />
    <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
      Saving {label}...
    </Box>
  </Box>
);

  if (!field) {
    return;
  }

  if (field.value === undefined || field.value === null) {
    field.value = "";
  }

  const handleUnlock = async () => {
    setForceViewLoading(true);
    setUnlockError("");
    try {
      const response = await HttpService.getAxiosClient().get(
        window.API_URL + `/${entity}/${entityId}/forceView/${fieldName}`
      );
      setUnlockedValue(response.data?.[fieldName]?.value ?? "");
      setUnlocked(true);
      setTimeout(() => {
        setUnlocked(false);
        setUnlockedValue("");
      }, 7000);
    } catch (e) {
      setUnlockError(`Not feasible to show the data for ${label}`);
    }
    setForceViewLoading(false);
  };

  const handleAccept = () => {
    setIsEditing(false);
    onChange && onChange(editValue);
  };

  const isLocked = field?.locked && !unlocked;

  if (editable && isEditing) {
    return (
      <Box display="flex" sx={{ width: "100%" }}>
        {editorType === "select" ? (
          <FormControl variant="standard" fullWidth>
            <InputLabel id={`select-label-${fieldName}`}>{label}</InputLabel>
            <Select
              labelId={`select-label-${fieldName}`}
              id={`select-${fieldName}`}
              fullWidth
              value={
                selectOptions.some((opt) => opt.value === editValue)
                  ? editValue
                  : ""
              }
              onChange={(e) => setEditValue(e.target.value)}
              label={label}
              open={selectOpen}
              onOpen={() => setSelectOpen(true)}
              onClose={() => setSelectOpen(false)}
              autoFocus
            >
              {selectOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          inputNumericMask ? (
            <NumericFormat
              sx={{ display: 'flex', alignItems: 'center'}}
              customInput={TextField}
              fullWidth={props.fullWidth !== false}
              value={editValue}
              variant="standard" 
              margin={props.margin || "dense"}
              onValueChange={(values) => setEditValue(values.value)}
              {...inputNumericMask}
              label={label}
              inputRef={inputRef}
              autoFocus
              onKeyDown={e => {
                if (e.key === "Enter") {
                  handleAccept();
                }
              }}
              {...props}
            />
          ) : (<InputMask
            mask={inputMask || ""}
            sx={{ display: 'flex', alignItems: 'center'}}
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
          >
            {(inputProps) => (
              <TextField
                {...props}
                {...inputProps}
                label={label}
                variant="standard"
                inputRef={inputRef}
                InputProps={{
                  sx: { "& .MuiInputBase-input": { fontSize: 14 } },
                }}
                InputLabelProps={{
                  sx: { fontSize: 14 },
                }}
                margin={props.margin || "dense"}
                fullWidth={props.fullWidth !== false}
                autoFocus
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    handleAccept();
                  }
                }}
              />
            )}
          </InputMask>
          )
        )}
        <Box display="flex" alignItems="flex-end" ml={1}>
          {editorType !== "select" || (editValue && editValue !== "") ? (
            <IconButton
              color="primary"
              onClick={() => {
                setIsEditing(false);
                onChange && onChange(editValue);
              }}
            >
              <CheckIcon />
            </IconButton>
          ) : null}
          <IconButton
            color="error"
            onClick={() => {
              setEditValue(field.value ?? "");
              setIsEditing(false);
              if (window.getSelection) window.getSelection().removeAllRanges();
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <TextField
      {...props}
      label={label}
      variant="standard"
      value={getDisplayValue()}
      // disabled
      InputProps={{
        sx: { "& .MuiInputBase-input": { fontSize: 14 } },
        endAdornment: (
          <>
            {isLocked && (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleUnlock}
                  size="small"
                  disabled={forceViewLoading}
                >
                  <VisibilityIcon />
                </IconButton>
              </InputAdornment>
            )}
            {editable && (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => {
                    setIsEditing(true);
                  }}
                  size="small"
                >
                  <EditIcon />
                </IconButton>
              </InputAdornment>
            )}
          </>
        ),
        readOnly: !props.onChange,
      }}
      InputLabelProps={{
        sx: { fontSize: 14 },
      }}
      margin={props.margin || "dense"}
      fullWidth={props.fullWidth !== false}
    />
  );
}
